
import { FarmProfile } from "./FarmProfile";
import { useProfileById } from "@/hooks/useProfileById";
import { useCropPlanByUserId } from "@/hooks/useCropPlanByUserId";
import { LoadingScreen } from "./LoadingScreen";
import { ErrorScreen } from "./ErrorScreen";

interface Farm {
  id: number;
  name: string;
  username: string;
  avatar: string;
  cover: string;
  location: {
    lat: number;
    lng: number;
    address: string;
  };
  crops: string[];
  distance: string;
  description: string;
  verified: boolean;
  category: string;
  calculatedDistance?: number;
  plantHardinessZone: string;
  userId?: string; // Add this to pass the actual user ID
}

interface DiscoverFarmProfileProps {
  farm: Farm;
  onBack: () => void;
}

export const DiscoverFarmProfile = ({ farm, onBack }: DiscoverFarmProfileProps) => {
  // Use the real user ID from the farm data to fetch actual profile
  const { profile, loading: profileLoading, error: profileError } = useProfileById(farm.userId || null);
  const { userCropPlans, loading: cropLoading, error: cropError } = useCropPlanByUserId(farm.userId || null);

  // Crop plans are optional for public viewing — never block the profile view
  const loading = profileLoading; // ignore cropLoading
  const error = profileError; // ignore cropError

  if (cropError) {
    // Log as non-fatal so we still render the profile
    console.warn('Non-fatal crop plans error in DiscoverFarmProfile:', cropError);
  }

  if (loading) {
    return (
      <div className="min-h-screen">
        <div className="pb-20">
          <div className="mb-3 px-3 pt-3">
            <button
              onClick={onBack}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              ← Back to Discover
            </button>
          </div>
          <LoadingScreen />
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen">
        <div className="pb-20">
          <div className="mb-3 px-3 pt-3">
            <button
              onClick={onBack}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              ← Back to Discover
            </button>
          </div>
          <ErrorScreen 
            authError={error || "Failed to load farm profile"} 
            onRetry={onBack} 
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="pb-20">
        <div className="mb-3 px-3 pt-3">
          <button
            onClick={onBack}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            ← Back to Discover
          </button>
        </div>
        <FarmProfile
          profile={profile}
          userCropPlansCount={userCropPlans.length}
          userCropPlans={userCropPlans}
        />
      </div>
    </div>
  );
};
